import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import Autocomplete from 'components/Common/Autocomplete';
import { ParishesAutocompleteProps } from 'types/autocomplete';
import { AppDispatch } from 'redux/store';
import AutocompleteOption from 'components/Common/autocomplete/AutocompleteOption';
import { Parish } from 'types/parishes';
import { fetchParishData } from 'redux/slices/parishesAndOrganisations/parishOrganizationSlice';
import { debounce } from 'utils/helpers';

const ParishesAutocomplete = (props: ParishesAutocompleteProps) => {
    const dispatch = useDispatch<AppDispatch>();
    const [loading, setLoading] = useState(false);
    const [parishes, setParishes] = useState<Parish[]>([]);
    const [searchText, setSearchText] = useState('');
    let { value, defaultSearchText } = props || {};

    const fetchParishes = useCallback(async () => {
        let search = searchText;
        // it is added or first time data fetching and need to disable the component because we just need to fetch one parish
        // otherwise if we do not disable then it will create a bug when user search for a parish that do not exist and list will be empty
        // so it will again update the search with default text.
        // else we can maintain a initialized state or ref to maintain that first time data fetching in case of default search
        if (!parishes?.length && defaultSearchText) {
            search = defaultSearchText;
        }
        try {
            setLoading(true);
            const { payload }: any = await dispatch(fetchParishData({ searchText: search, headers: { pageSize: 50, pageNumber: 0 } }));
            setLoading(false);
            const _parishes = payload?.data;
            setParishes(_parishes);
        } catch (e) {}
    }, [dispatch, searchText, defaultSearchText]); // eslint-disable-line

    useEffect(() => {
        fetchParishes();
    }, [dispatch, fetchParishes, searchText]);

    return (
        <Autocomplete
            loading={loading}
            options={parishes}
            value={value}
            getOptionLabel={(option) => (loading ? 'Loading...' : option?.name)}
            placeholder="Please type to search..."
            renderOption={(props: any, option: any) => <AutocompleteOption option={{ ...option, imgUrl: option?.logoUrl }} props={props} />}
            getOptionKey={(option) => (typeof option === 'object' ? option?.companyId : option)}
            noOptionsText={searchText && loading ? 'Loading...' : searchText ? 'No options' : 'Please type to search...'}
            onInputChange={debounce((_: any, searchText: any, reason: any) => {
                // changing the search text only when user type the input or clear the input value, so no extra api trigger on selection
                if (reason === 'input') {
                    setSearchText(searchText);
                } else if (reason === 'clear') {
                    setSearchText('');
                }
            }, 700)}
            {...props}
        />
    );
};

export default ParishesAutocomplete;
